import { Send, Check, AlertCircle } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Spinner from '../ui/Spinner';
import { useNewsletterSubscribe } from '../../hooks/useNewsletterSubscribe';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: ${(props) => props.theme.borderRadius['2xl']};
  background-color: ${(props) => props.theme.colors.newsletter.background};
  color: ${(props) => props.theme.colors.newsletter.foreground};
  border: 1px solid ${(props) => props.theme.colors.border};
  padding: 2rem;

  @media (min-width: 768px) {
    padding: 3rem;
  }

  @media (min-width: 1024px) {
    padding: 4rem;
  }
`;

const DecorativeBlur1 = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 16rem;
  height: 16rem;
  background-color: ${(props) => props.theme.colors.category.coral};
  opacity: 0.18;
  border-radius: 9999px;
  filter: blur(64px);
  pointer-events: none;
`;

const DecorativeBlur2 = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 12rem;
  height: 12rem;
  background-color: ${(props) => props.theme.colors.category.teal};
  opacity: 0.18;
  border-radius: 9999px;
  filter: blur(64px);
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  max-width: 36rem;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h2`
  font-family: ${(props) => props.theme.typography.fonts.display};
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  color: ${(props) => props.theme.colors.newsletter.foreground};
  margin-bottom: 0.75rem;
`;

const Description = styled.p`
  color: ${(props) => props.theme.colors.newsletter.muted};
  margin-bottom: 1.5rem;
  font-size: clamp(0.9375rem, 1.5vw, 1.125rem);
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  height: 2.75rem;
  padding: 0 1rem;
  border-radius: ${(props) => props.theme.borderRadius['2xl']};
  background-color: ${(props) => props.theme.colors.newsletter.inputBackground};
  border: 1px solid ${(props) => props.theme.colors.newsletter.inputBorder};
  color: ${(props) => props.theme.colors.newsletter.foreground};
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: ${(props) => props.theme.colors.newsletter.inputPlaceholder};
  }

  &:focus-visible {
    border-color: ${(props) => props.theme.colors.newsletter.foreground}66;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.newsletter.foreground}22;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubscribeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 8rem;
  height: 2.75rem;
  padding: 0 1.5rem;
  border: none;
  border-radius: ${(props) => props.theme.borderRadius['2xl']};
  background-color: ${(props) => props.theme.colors.newsletter.buttonBackground};
  color: ${(props) => props.theme.colors.newsletter.buttonForeground};
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: ${(props) => props.theme.borderRadius.md};
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  ${(props) =>
    props.$type === 'success' &&
    `
    background-color: ${props.theme.colors.success}22;
    color: ${props.theme.colors.success};
    border: 1px solid ${props.theme.colors.success}55;
  `}

  ${(props) =>
    props.$type === 'error' &&
    `
    background-color: ${props.theme.colors.error}22;
    color: ${props.theme.colors.error};
    border: 1px solid ${props.theme.colors.error}55;
  `}

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`;

export default function Newsletter() {
  const { t } = useTranslation();
  const { email, setEmail, isSubmitting, message, messageType, submit } = useNewsletterSubscribe();

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submit();
  };

  return (
    <Section id="newsletter">
      <DecorativeBlur1 />
      <DecorativeBlur2 />
      <Content>
        <Title>{t('newsletter.heading')}</Title>
        <Description>{t('newsletter.description')}</Description>

        <Form onSubmit={handleSubmit}>
          <EmailInput
            type="email"
            placeholder={t('newsletter.placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            required
            aria-label={t('newsletter.placeholder')}
          />
          <SubscribeButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" />
                {t('common.loading')}
              </>
            ) : (
              <>
                {t('newsletter.button')}
                <Send size={18} />
              </>
            )}
          </SubscribeButton>
        </Form>

        {message && (
          <Message $type={messageType}>
            {messageType === 'success' ? <Check /> : <AlertCircle />}
            {message}
          </Message>
        )}
      </Content>
    </Section>
  );
}
